import * as React from 'react';
import { t } from '@reos/i18n';
import type { CaseFreshness } from '../lib/case-cockpit.js';
import type { ReviewLevel } from '../lib/audit-api.js';
import { ReviewLevelChoice, reviewConfirmBodyFor, type ReviewOptions } from './ReviewLevelChoice.js';

const cs = { locale: 'cs' } as const;

/**
 * Krok UX2 — HLAVIČKA O AKTUÁLNOSTI (záložka Kokpit).
 *
 * Nález R.B. 29. 9. 2026: průzkum Poděbradova byl z 1. 9. a „nic to neříká“ —
 * makléř netušil, že poslední kontrola je starý měsíc. Tady je datum
 * posledního DOKONČENÉHO běhu, přes 14 dním štítek a tlačítko na nový běh.
 *
 * HROMADNÉ PŘEPROVĚŘOVÁNÍ SE NEDĚLÁ (zadání UX2): stojí peníze. Tlačítko je
 * jedno a výslovně potvrzované.
 */
export function CaseFreshnessBar({
  freshness,
  onRecheck,
  rechecking,
  recheckError,
  publicDemo = false,
  reviewOptions = null,
}: {
  readonly freshness: CaseFreshness;
  /**
   * Krok ZDARMA1: beh dostava ROZSAH. Bez argumentu by karta Prověření
   * spustila jinou uroven, nez makler vybral.
   */
  readonly onRecheck: (level: ReviewLevel) => void;
  readonly rechecking: boolean;
  readonly recheckError: string | null;
  /** Krok DEMO7: v demu je proverení zdarma, takze text o kreditu neplati. */
  readonly publicDemo?: boolean;
  /**
   * Krok NOC6 body 3+4 (N27, N28): cena kompletniho provereni, kredit
   * kancelare a zbytek stropu. Tataz cisla jako u prvniho spusteni v detailu
   * zakazky — „Prověřit znovu" je druhe misto, kde se rozsah vybira, a dve
   * ruzne ceny u jedne volby jsou presne to, co nalez N27 hlasi.
   */
  readonly reviewOptions?: ReviewOptions | null;
}): React.JSX.Element {
  const [confirming, setConfirming] = React.useState(false);
  /**
   * Krok ZDARMA1 bod 1 — vychozi je BEZPLATNA zakladni uroven, stejne jako
   * u prvniho spusteni v detailu zakazky (`REVIEW_LEVEL_DEFAULT`). Hodnota
   * je tady ve stavu karty: prepnuti rozsahu nema nacitat zakazku znovu.
   */
  const [level, setLevel] = React.useState<ReviewLevel>('zakladni');

  return (
    <div
      className="flex flex-wrap items-center gap-3 mb-3"
      data-testid={freshness.testId}
      data-stale={String(freshness.stale)}
    >
      <div>
        {/* Krok D2: popisek je `Prověřeno` jen tehdy, když to data unesou —
            jinak `Zdroje čteny`. Pravidlo je jedno (`reviewed-state.ts`). */}
        <div className="rh-a2-meta" style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {freshness.caption}
        </div>
        {/* Krok FIX6 bod 5: hodnota BEZ slova „Prověřeno“ — popisek nad ní je
            z téhož katalogového klíče, takže se slovo psalo dvakrát. */}
        <div style={{ fontWeight: 'var(--rh-heading-weight)' }} data-testid="case-freshness-date">
          {freshness.value}
        </div>
      </div>
      {freshness.stale && (
        <span className="rh-a2-badge rh-a2-badge--wait" data-testid="case-freshness-stale-badge">
          {t('webAdmin.caseCockpit.freshness.staleBadge', cs)}
        </span>
      )}
      {/* Krok D2: místo uklidňujícího slova se jmenuje, co ještě chybí. */}
      {!freshness.reviewed && freshness.missing.length > 0 && (
        <span className="rh-a2-meta" data-testid="case-freshness-missing">
          <strong>{t('webAdmin.caseCockpit.freshness.missingHeading', cs)}:</strong>{' '}
          {freshness.missing.join(', ')}
        </span>
      )}
      {!confirming ? (
        <button
          type="button"
          className="rh-a2-btn"
          disabled={rechecking}
          onClick={() => { setConfirming(true); }}
          data-testid="case-freshness-recheck"
        >
          {t('webAdmin.caseCockpit.freshness.recheck', cs)}
        </button>
      ) : (
        <div
          className="flex flex-wrap items-center gap-2"
          role="group"
          aria-label={t('webAdmin.caseCockpit.freshness.confirmTitle', cs)}
          data-testid="case-freshness-confirm"
        >
          {/* Krok ZDARMA1 bod 1: rozsah se vybira TADY, nad potvrzenim. */}
          <ReviewLevelChoice
            value={level}
            onChange={setLevel}
            disabled={rechecking}
            testId="case-freshness-level"
            options={reviewOptions}
            hideBasicQuota={publicDemo}
          />
          <span className="rh-a2-meta" data-testid="case-freshness-confirm-body">
            <strong>{t('webAdmin.caseCockpit.freshness.confirmTitle', cs)}</strong>{' '}
            {reviewConfirmBodyFor(level, publicDemo, reviewOptions)}
          </span>
          <button
            type="button"
            className="rh-a2-btn rh-a2-btn--primary"
            disabled={rechecking}
            onClick={() => { setConfirming(false); onRecheck(level); }}
            data-testid="case-freshness-recheck-accept"
          >
            {t('webAdmin.caseCockpit.freshness.confirmAccept', cs)}
          </button>
          <button
            type="button"
            className="rh-a2-btn"
            onClick={() => { setConfirming(false); }}
            data-testid="case-freshness-recheck-cancel"
          >
            {t('webAdmin.caseCockpit.freshness.confirmCancel', cs)}
          </button>
        </div>
      )}
      {/*
        KROK ZDARMA1 bod 2 — UPSELL BEZ TLAKU.
        Vidi ho makler, ktery ma vybranou BEZPLATNOU uroven (tedy po zakladnim
        behu, dokud si kompletni nevybral): jedna veta, co kompletni proverení
        ZJISTI NAVIC, a tlacitko. Tlacitko NIC NESPOUSTI — jen prepne rozsah
        a otevre tytez potvrzeni jako „Prověřit znovu" (zadani: „bez
        automatickeho spusteni"). Tataz veta je v podrobnem reportu
        (`dd.report.zakladni.*`, zamcena sekce z kroku ZR1).
      */}
      {!confirming && level === 'zakladni' && (
        <div className="rh-a2-meta" data-testid="case-review-upsell">
          <strong>{t('webAdmin.caseDetailV4.upsellTitle', cs)}</strong>{' '}
          {t('webAdmin.caseDetailV4.upsellNote', cs)}
          <button
            type="button"
            className="rh-a2-btn"
            disabled={rechecking}
            onClick={() => { setLevel('plny'); setConfirming(true); }}
            data-testid="case-review-upsell-action"
          >
            {t('webAdmin.caseDetailV4.upsellAction', cs)}
          </button>
        </div>
      )}
      {rechecking && (
        <span role="status" className="rh-a2-meta" data-testid="case-freshness-queued">
          {t('webAdmin.caseCockpit.freshness.queued', cs)}
        </span>
      )}
      {recheckError !== null && (
        <span role="alert" className="rh-a2-meta" style={{ color: 'var(--rh-danger)' }} data-testid="case-freshness-error">
          {t('webAdmin.caseCockpit.freshness.failed', { ...cs, values: { error: recheckError } })}
        </span>
      )}
    </div>
  );
}
